import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ScreenStatus } from '@seencast/shared';
import { useScreens } from '../../api/screens.ts';
import { useGroups } from '../../api/groups.ts';
import { useMediaNameResolver } from '../../api/media.ts';
import { usePermissions } from '../../auth/session.tsx';
import { Button, Chip, StatusDot, Loading, ErrorState, EmptyState } from '../../components/ui.tsx';
import { useLivePresence } from '../../live/LivePresenceContext.tsx';
import { liveStatusForScreen, nowPlayingLabel } from '../../live/presence.ts';
import { formatRelative } from '../../lib/format.ts';
import { ScreenPairIcon } from '../../onboarding/icons.tsx';
import { PairScreenModal } from './PairScreenModal.tsx';
import { GroupsModal } from './GroupsModal.tsx';
import { ScreenDetailDrawer } from './ScreenDetailDrawer.tsx';

export interface ScreensSearch {
  screen?: string;
  pair?: boolean;
  code?: string;
  groups?: boolean;
  status?: ScreenStatus;
  q?: string;
  group?: string;
}

const STATUS_TABS: Array<{ value?: ScreenStatus; label: string }> = [
  { value: undefined, label: 'All' },
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
  { value: 'never_paired', label: 'Not paired' },
];

export function ScreensPage() {
  const search = useSearch({ strict: false }) as ScreensSearch;
  const navigate = useNavigate();
  const perms = usePermissions();

  const setSearch = (patch: Partial<ScreensSearch>) =>
    navigate({ to: '/screens', search: (prev) => ({ ...prev, ...patch }) });

  const filters = { status: search.status, group_id: search.group, q: search.q };
  const screens = useScreens(filters);
  const groups = useGroups();
  const { presence } = useLivePresence();
  const resolveMediaName = useMediaNameResolver();

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Screens</h1>
          <div className="subtitle">Every display in your fleet and what it&apos;s showing.</div>
        </div>
        {perms.canManageContent && (
          <div className="row">
            <Button onClick={() => setSearch({ groups: true })}>Manage groups</Button>
            <Button variant="primary" onClick={() => setSearch({ pair: true })}>
              Pair new screen
            </Button>
          </div>
        )}
      </div>

      <div className="toolbar">
        <input
          className="search"
          aria-label="Search screens"
          placeholder="Search name or location…"
          value={search.q ?? ''}
          onChange={(e) => setSearch({ q: e.target.value || undefined })}
        />
        <div className="seg" role="tablist" aria-label="Filter by status">
          {STATUS_TABS.map((t) => (
            <button
              key={t.label}
              className={search.status === t.value ? 'active' : ''}
              onClick={() => setSearch({ status: t.value })}
            >
              {t.label}
            </button>
          ))}
        </div>
        {(groups.data?.length ?? 0) > 0 && (
          <select
            aria-label="Filter by group"
            value={search.group ?? ''}
            onChange={(e) => setSearch({ group: e.target.value || undefined })}
            style={{ width: 'auto' }}
          >
            <option value="">All groups</option>
            {groups.data!.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {screens.isLoading ? (
        <Loading />
      ) : screens.isError ? (
        <ErrorState message="Could not load screens." onRetry={() => screens.refetch()} />
      ) : screens.data!.length === 0 && !search.q && !search.status && !search.group ? (
        <ScreensEmpty canPair={perms.canManageContent} onPair={() => setSearch({ pair: true })} />
      ) : screens.data!.length === 0 ? (
        <div className="card card-pad text-dim">No screens match these filters.</div>
      ) : (
        <div className="table-wrap cards">
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Groups</th>
                <th>Status</th>
                <th>Last seen</th>
                <th>Now playing</th>
              </tr>
            </thead>
            <tbody>
              {screens.data!.map((s) => (
                <tr key={s.id} className="clickable" onClick={() => setSearch({ screen: s.id })}>
                  <td>
                    <div className="cell-strong">{s.name}</div>
                    <div className="cell-dim">{s.location}</div>
                  </td>
                  <td data-label="Groups">
                    <div className="chip-row">
                      {s.groups.map((g) => (
                        <Chip key={g.id}>{g.name}</Chip>
                      ))}
                    </div>
                  </td>
                  <td data-label="Status">
                    <StatusDot status={liveStatusForScreen(s, presence)} />
                  </td>
                  <td className="cell-dim" data-label="Last seen">
                    {formatRelative(s.last_seen_at)}
                  </td>
                  <td data-label="Now playing">
                    {nowPlayingLabel(s, presence, resolveMediaName) ?? <span className="cell-dim">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(search.pair || search.code) && (
        <PairScreenModal
          initialCode={search.code ?? ''}
          onClose={() => setSearch({ pair: undefined, code: undefined })}
          onPaired={(id) => setSearch({ pair: undefined, code: undefined, screen: id })}
        />
      )}
      {search.groups && <GroupsModal onClose={() => setSearch({ groups: undefined })} />}
      {search.screen && (
        <ScreenDetailDrawer screenId={search.screen} onClose={() => setSearch({ screen: undefined })} />
      )}
    </div>
  );
}

function ScreensEmpty({ canPair, onPair }: { canPair: boolean; onPair: () => void }) {
  return (
    <div className="card card-pad">
      <EmptyState icon={<ScreenPairIcon />} title="Pair your first screen">
        <p>Get any display showing your content in three steps:</p>
        <div className="steps">
          <div className="step">
            <span className="num">1</span>
            <div>Open the player URL on the screen (TV, media stick, or browser).</div>
          </div>
          <div className="step">
            <span className="num">2</span>
            <div>It shows a 6-character pairing code.</div>
          </div>
          <div className="step">
            <span className="num">3</span>
            <div>Click “Pair new screen” and enter that code.</div>
          </div>
        </div>
        <div className="code-figure">
          <div className="text-dim" style={{ fontSize: 'var(--fs-sm)', marginBottom: 8 }}>
            The screen will look like this:
          </div>
          <div className="code-glyphs">7KQP3M</div>
        </div>
        {canPair && (
          <div className="mt-4">
            <Button variant="primary" onClick={onPair}>
              Pair new screen
            </Button>
          </div>
        )}
      </EmptyState>
    </div>
  );
}
